/* eslint-disable @typescript-eslint/no-empty-function */
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../app/auth/auth.service';
import {
  AdditionalInformationInput,
  additionnalsInformationsInputType,
  ApplicationBoardFilter,
  Company,
  FilterDirection,
  JobBoard,
  SearchFilter,
  searchQueryFilter,
  SortDirection,
  TranslateKey,
} from '@job-v2/ats-utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { INPUT_NUMBER_FILTER_OPTIONS } from '../../app/commons/contants';
import { CommonModule } from '@angular/common';
import { AppGlobalService } from '../../app/commons/services/app-global.service';
import { BoardService } from '../../app/management/board/board.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputGroupModule } from 'primeng/inputgroup';
import { MenuModule } from 'primeng/menu';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { JobBoardFilter } from '@job-v2/shared';

@Component({
  imports: [
    RouterModule,
    SelectModule,
    MultiSelectModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    DatePickerModule,
    ButtonModule,
    MenuModule,
    FloatLabelModule,
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrl: './toolbar.component.scss',
})
export class ToolBarComponent implements OnInit, OnDestroy {
  public readonly authService = inject(AuthService);
  private readonly subscription = new Subscription();
  readonly appGlobalService = inject(AppGlobalService);
  private readonly destroy$ = new Subject<void>();
  private readonly boardService = inject(BoardService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  public readonly company: Company | null = null;

  public readonly translateKey = TranslateKey;

  isCollapsed = false;

  job: JobBoard | undefined;
  infosAddsType = additionnalsInformationsInputType;
  dropdownOptions = INPUT_NUMBER_FILTER_OPTIONS;
  pagesWithSearchBar = ['/management/jobs/board'];
  filteredInfosAdditionals: AdditionalInformationInput[] = [];

  ngOnInit(): void {
    this.boardService.boardJobSource$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.translateReferencesData()
          this.job = data;
          this.filteredInfosAdditionals =
            this.job?.jobDetails.additionalsInformations.filter(
              (t) => t.type !== additionnalsInformationsInputType.INPUT_TEXT,
            ) || [];
        }
      });
  }

  translateReferencesData() {
    this.dropdownOptions = this.dropdownOptions.map(option => ({
      ...option,
      label: this.translateService.instant(option.label)
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription.unsubscribe();
  }

  isSearchBarVisible() {
    return !!this.pagesWithSearchBar.some(
      (route) => this.router.url.startsWith(route) || route === this.router.url,
    );
  }

  applicationBoardFilters: ApplicationBoardFilter = {
    pagination: {
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortByDirection: SortDirection.ASC,
      filters: undefined,
    },
    queries: [],
  };
  inputText = '';
  strictLimiter = 0;
  limitFilter = -1;
  directionFilter: FilterDirection | string = '';
  inputNumberFilter: Record<string, number> = {};
  inputNumberIntervalFilter: Record<string, searchQueryFilter> = {};
  inputCheckBoxFilter: Record<string, Array<string>> = {};
  inputSelectFilter: Record<string, Array<string>> = {};
  inputRadioFilter: Record<string, Array<string>> = {};
  inputStartDateFilter: Record<string, Date> = {};
  inputEndDateFilter: Record<string, Date> = {}
  setValue = 0;
  percentagesOptions = [
    { label: 'N/A', value: -1 },
    { label: '10 %', value: 10 },
    { label: '25 %', value: 25 },
    { label: '50 %', value: 50 },
    { label: '75 %', value: 75 },
    { label: '90 %', value: 90 },
  ];
  directionsOptions = [
    { label: 'N/A', value: '' },
    {
      label: this.translateService.instant(
        this.translateKey.BOARD_FILTER_DIRECTION_FIRST,
      ),
      value: FilterDirection.FIRST,
    },
    {
      label: this.translateService.instant(
        this.translateKey.BOARD_FILTER_DIRECTION_LAST,
      ),
      value: FilterDirection.LAST,
    },
  ];

  returnInfosAddValue(
    value: string | string[],
    type: additionnalsInformationsInputType,
  ): string[] {
    if (
      type === additionnalsInformationsInputType.INPUT_RADIO ||
      type === additionnalsInformationsInputType.INPUT_SELECT ||
      type === additionnalsInformationsInputType.INPUT_CHECKBOX
    )
      return value as string[];
    return [];
  }

  setMultipleValueOption(value: string[]) {
    return (
      value?.map((item) => ({
        label: item,
        value: item,
      })) ?? []
    );
  }

  getTitleIndex(sequence: number, inputType: additionnalsInformationsInputType): number {
    return (
      this.filteredInfosAdditionals
      .filter((v) => v.type === inputType)
      .findIndex((i) => i.sequence === sequence)
    );
  }

  resetBoardSearch() {
    this.inputNumberFilter = {};
    this.inputCheckBoxFilter = {};
    this.inputSelectFilter = {};
    this.inputRadioFilter = {};
    this.inputStartDateFilter = {};
    this.inputEndDateFilter = {};
    this.inputText = '';
    this.strictLimiter = 0;
    this.limitFilter = -1;
    this.directionFilter = '';
    this.appGlobalService._BoardJobFilterSource.next(undefined);
    this.appGlobalService._resetBoardSource.next(true);
  }

  setPaginationFilters() {
    this.applicationBoardFilters.pagination.filters = {
      percentage: this.strictLimiter > 0 ? -1 : this.limitFilter,
    };
    if (this.strictLimiter) {
      this.applicationBoardFilters.pagination.limit = this.strictLimiter;
    } else {
      this.applicationBoardFilters.pagination.limit = 10;
    }
    if (this.directionFilter?.length > 0) {
      this.applicationBoardFilters.pagination.sortByDirection =
        this.directionFilter === FilterDirection.FIRST
          ? SortDirection.DESC
          : SortDirection.ASC;
      this.applicationBoardFilters.pagination.sortBy =
        JobBoardFilter.CALCULATED_SCORE;
    }
  }

  async setBoardFilter() {
    const queries: SearchFilter[] = [];
    this.setPaginationFilters();
    if (this.inputText.length > 0) {
      queries.push({
        field: `candidateDetails`,
        operator: '=',
        value: this.inputText,
      });
    }
    if (Object.keys(this.inputNumberFilter).length > 0) {
      Object.keys(this.inputNumberFilter).forEach((i) => {
        const value = this.inputNumberFilter[Number(i)];
        if (value && !isNaN(value)) {
          queries.push({
            field: `additionalInformations.${additionnalsInformationsInputType.INPUT_NUMBER}.${i}.value`,
            operator: this.inputNumberIntervalFilter[Number(i)],
            value: value.toString(),
          });
        }
      });
    }
    if (Object.keys(this.inputCheckBoxFilter)?.length > 0) {
      Object.keys(this.inputCheckBoxFilter).forEach((i) => {
        const value = this.inputCheckBoxFilter[Number(i)];
        if (value?.length > 0) {
          queries.push({
            field: `additionalInformations.${additionnalsInformationsInputType.INPUT_CHECKBOX}.${i}.value`,
            operator: value.length >= 2 ? 'contains' : 'strictly-contains',
            value: value,
          });
        }
      });
    }
    if (Object.keys(this.inputSelectFilter)?.length > 0) {
      Object.keys(this.inputSelectFilter).forEach((i) => {
        const value = this.inputSelectFilter[Number(i)];
        if (value?.length > 0) {
          queries.push({
            field: `additionalInformations.${additionnalsInformationsInputType.INPUT_SELECT}.${i}.value`,
            operator: value.length >= 2 ? 'contains' : 'strictly-contains',
            value: value as string[],
          });
        }
      });
    }
    if (Object.keys(this.inputRadioFilter)?.length > 0) {
      Object.keys(this.inputRadioFilter).forEach((i) => {
        const value = this.inputRadioFilter[Number(i)];
        if (value?.length > 0) {
          queries.push({
            field: `additionalInformations.${additionnalsInformationsInputType.INPUT_RADIO}.${i}.value`,
            operator: value.length >= 2 ? 'contains' : 'strictly-contains',
            value: value,
          });
        }
      });
    }
    if (Object.keys(this.inputStartDateFilter)?.length > 0 && Object.keys(this.inputEndDateFilter)?.length > 0) {
      Object.keys(this.inputStartDateFilter).forEach((i) => {
        const startDate = this.inputStartDateFilter[Number(i)].toISOString();
        const endDate = this.inputEndDateFilter[Number(i)].toISOString()
        if (startDate && endDate) {
          queries.push({
            field: `additionalInformations.${additionnalsInformationsInputType.INPUT_DATE}.${i}.value`,
            operator: 'between',
            value: `${startDate},${endDate}`,
          });
        }
      });
    }
    this.applicationBoardFilters.queries = queries;
    this.appGlobalService._BoardJobFilterSource.next(
      this.applicationBoardFilters,
    );
    this.appGlobalService._resetBoardSource.next(false);
  }
}
